import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  Row,
  Col,
  Input,
  FormGroup
} from 'reactstrap';
// import TableContainer from 'Components/Common/TableContainerReactTable';
import TableContainerReactTable from 'Components/Common/TableContainerReactTable';
import FormInput from '../../common/FormInput';
import { toast } from 'react-toastify';
import { FeatureTabSizeModal } from '../modals/FeatureTabSizeModal';
import { ApplyTemplateModal } from '../modals/ApplyTemplateModal';
import { useGetProductConfiguratorFeatureMatrixLabelsQuery, useGetProductConfiguratorFeatureMatrixValuesQuery } from '../../../redux/productApiSlice';

interface FeatureMatrix {
  [size: string]: {
    [color: string]: boolean;
  };
}

interface FeatureCombination {
  size: string;
  color: string;
  enabled: boolean;
}

interface TableRowData {
  color: string;
  [size: string]: boolean | string; // Dynamic size columns
}

const FeaturesTab: React.FC<{ setIsPendingChanges: (isPendingChanges: boolean) => void, productRowId: number }> = ({ setIsPendingChanges, productRowId }) => {
  // Available sizes and colors - will be populated from API
  const [sizes, setSizes] = useState<string[]>([]);
  const [colors, setColors] = useState<string[]>([]);

  // Feature Tab Size Modal state
  const [isFeatureTabSizeModalOpen, setIsFeatureTabSizeModalOpen] = useState(false);
  
  //Apply Template Modal state
  const [isApplyTemplateModalOpen, setIsApplyTemplateModalOpen] = useState(false);


  // Redux APIs for Feature label and Feature Matrix Values
  const { 
    data: featureLabels, 
    isLoading: isFeatureLabelsLoading, 
    error: featureLabelsError 
  } = useGetProductConfiguratorFeatureMatrixLabelsQuery(
    { productRowId },
    { skip: !productRowId || productRowId === 0 }
  );
  const { 
    data: featureMatrixValues, 
    isLoading: isFeatureMatrixValuesLoading, 
    error: featureMatrixValuesError 
  } = useGetProductConfiguratorFeatureMatrixValuesQuery(
    { productRowId },
    { skip: !productRowId || productRowId === 0 }
  );


  console.log('featureLabels', featureLabels);
  console.log('featureMatrixValues', featureMatrixValues);

  // Matrix state for feature combinations
  const [featureMatrix, setFeatureMatrix] = useState<FeatureMatrix>({});

  // Extract labels from API response
  const sizeLabel = featureLabels?.data?.[0]?.sizeLabel || 'Size';
  const colourLabel = featureLabels?.data?.[0]?.colourLabel || 'Colour';

  // Initialize sizes, colors, and matrix from API data
  useEffect(() => {
    if (featureMatrixValues?.data && featureMatrixValues.data.length > 0) {
      const matrixData = featureMatrixValues.data;

      // Extract unique sizes, ordered by sizeOrder
      const uniqueSizes = Array.from(
        new Map(
          matrixData
            .map(item => ({ size: item.size, sizeOrder: item.sizeOrder }))
            .sort((a, b) => a.sizeOrder - b.sizeOrder)
            .map(item => [item.size, item])
        ).values()
      ).map(item => item.size);

      // Extract unique colors (excluding empty strings)
      const uniqueColors = Array.from(
        new Set(
          matrixData
            .map(item => item.colour)
            .filter(colour => colour && colour.trim() !== '')
        )
      );

      // Build the feature matrix
      const newMatrix: FeatureMatrix = {};
      
      // Initialize all size-color combinations as false
      uniqueSizes.forEach(size => {
        newMatrix[size] = {};
        uniqueColors.forEach(color => {
          newMatrix[size][color] = false;
        });
      });

      // Mark existing combinations as true only if productId is available (non-zero)
      matrixData.forEach(item => {
        if (item.size && item.colour && item.colour.trim() !== '' && item.productId && item.productId !== 0) {
          if (!newMatrix[item.size]) {
            newMatrix[item.size] = {};
          }
          newMatrix[item.size][item.colour] = true;
        }
      });

      setSizes(uniqueSizes);
      setColors(uniqueColors);
      setFeatureMatrix(newMatrix);
    }
  }, [featureMatrixValues]);

  // Form inputs for adding new features
  const [newSize, setNewSize] = useState<string>('');
  const [newColor, setNewColor] = useState<string>('');

  // Handle checkbox changes in the matrix
  const handleMatrixChange = useCallback((size: string, color: string) => {
    setFeatureMatrix(prev => ({
      ...prev,
      [size]: {
        ...prev[size],
        [color]: !prev[size][color]
      }
    }));
  }, []);

  // Transform matrix data to table format
  const tableData = useMemo((): TableRowData[] => {
    return colors.map(color => {
      const row: TableRowData = { color };
      sizes.forEach(size => {
        row[size] = featureMatrix[size]?.[color] || false;
      });
      return row;
    });
  }, [colors, sizes, featureMatrix]);

  // Define table columns
  const columns = useMemo(() => {
    const baseColumns = [
      {
        header: colourLabel,
        accessorKey: 'color',
        enableColumnFilter: false,
        cell: ({ getValue }: any) => (
          <span className="fw-medium text-capitalize">{getValue()}</span>
        ),
      }
    ];


    // Add size columns dynamically
    const sizeColumns = sizes.map(size => ({
      header: size,
      accessorKey: size,
      enableColumnFilter: false,
      cell: ({ getValue, row }: any) => (
        <div className="d-flex justify-content-center">
          <FormGroup check className="mb-0">
            <Input
              type="checkbox"
              checked={getValue() || false}
              onChange={() => handleMatrixChange(size, row.original.color)}
              className="form-check-input"
            />
          </FormGroup>
        </div>
      ),
    }));

    return [...baseColumns, ...sizeColumns];
  }, [sizes, handleMatrixChange, colourLabel]);

  // Handle adding new size
  const handleAddSize = useCallback(() => {
    if (newSize.trim() && !sizes.includes(newSize.trim())) {
      // Add to sizes array
      setSizes(prev => [...prev, newSize.trim()]);

      // Add to featureMatrix
      setFeatureMatrix(prev => ({
        ...prev,
        [newSize.trim()]: colors.reduce((acc, color) => {
          acc[color] = false;
          return acc;
        }, {} as { [color: string]: boolean })
      }));
      setIsPendingChanges(true);
      setNewSize('');
    }
  }, [newSize, sizes, colors, setSizes, setFeatureMatrix]);

  // Handle adding new color
  const handleAddColor = useCallback(() => {
    const trimmedColor = newColor.trim();
    if (trimmedColor && !colors.includes(trimmedColor)) {
      // Add to colors array
      setColors(prev => [...prev, trimmedColor]);

      // Add the new color to every size in the featureMatrix with false
      setFeatureMatrix(prev => {
        const updated: FeatureMatrix = {};
        Object.keys(prev).forEach(sizeKey => {
          updated[sizeKey] = {
            ...prev[sizeKey],
            [trimmedColor]: false
          };
        });
        return updated;
      });

      setIsPendingChanges(true);
      setNewColor('');
    }
  }, [newColor, colors, setColors, setFeatureMatrix, setIsPendingChanges]);

  // Action handlers
  const handleRemoveSize = useCallback(() => {
    const trimmedSize = newSize.trim();
    if (!trimmedSize || !sizes.includes(trimmedSize)) {
      toast.error('Size not found');
      return;
    }

    // Remove from sizes list
    setSizes(prev => prev.filter(size => size !== trimmedSize));

    // Remove the size key from the matrix immutably
    setFeatureMatrix(prev => {
      const { [trimmedSize]: _removed, ...rest } = prev;
      return rest;
    });

    setIsPendingChanges(true);
    setNewSize('');
  }, [newSize, sizes, setSizes, setFeatureMatrix, setIsPendingChanges]);

  const handleRemoveColor = useCallback(() => {
    const trimmedColor = newColor.trim();
    if (!trimmedColor || !colors.includes(trimmedColor)) {
      toast.error('Color not found');
      return;
    }

    // Remove from colors list
    setColors(prev => prev.filter(color => color !== trimmedColor));

    // Remove the color key from every size entry in the matrix immutably
    setFeatureMatrix(prev => {
      const updated: FeatureMatrix = {};
      Object.keys(prev).forEach(sizeKey => {
        const { [trimmedColor]: _removed, ...restColors } = prev[sizeKey];
        updated[sizeKey] = restColors;
      });
      return updated;
    });

    setIsPendingChanges(true);
    setNewColor('');
  }, [newColor, colors, setColors, setFeatureMatrix, setIsPendingChanges]);

  const handleReOrderAndRenameSize = useCallback(() => {
    setIsFeatureTabSizeModalOpen(true);
  }, []);

  const handleSelectAll = useCallback(() => {
    setFeatureMatrix(prev => {
      const updated = { ...prev };
      Object.keys(updated).forEach(size => {
        Object.keys(updated[size]).forEach(color => {
          updated[size][color] = true;
        });
      });
      return updated;
    });
  }, []);

  const handleClearAllChildren = useCallback(() => {
    setFeatureMatrix(prev => {
      const updated = { ...prev };
      Object.keys(updated).forEach(size => {
        Object.keys(updated[size]).forEach(color => {
          updated[size][color] = false;
        });
      });
      return updated;
    });
  }, []);

  const handleRemoveAll = useCallback(() => {
    setSizes([]);
    setColors([]);
    setFeatureMatrix({});
    setIsPendingChanges(true);
    setNewSize('');
    setNewColor('');
  }, []);

  const handleShowMore = useCallback(() => {
    // setShowMore(!showMore);
  }, []);

  return (
    <div className="features-tab p-0">
      <Row>
        <Col>
          <fieldset className="border rounded m-1 p-2">
            {/* Action Buttons Row */}
            <Row className="mt-1 mb-2">
              <Col className="d-flex justify-content-center gap-2">
                <button
                  type="button"
                  className="btn btn-soft-secondary btn-sm"
                  onClick={() => setIsApplyTemplateModalOpen(true)}
                >
                  Apply Template
                </button>
                <button
                  type="button"
                  className="btn btn-soft-secondary btn-sm"
                  onClick={() => console.log('Save as Template clicked')}
                >
                  Save as Template
                </button>
                <button
                  type="button"
                  className="btn btn-soft-primary btn-sm"
                  onClick={() => console.log('Switch to Associate clicked')}
                >
                  Switch to Associate
                </button>
              </Col>
            </Row>

            {/* Feature Matrix Table */}
            {(isFeatureLabelsLoading || isFeatureMatrixValuesLoading) ? (
              <div className="text-center p-3">
                <div className="spinner-border spinner-border-sm text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <span className="ms-2">Loading feature matrix...</span>
              </div>
            ) : (
              <div className="table-responsive mb-1">
                <TableContainerReactTable
                  isGlobalFilter={false}
                  columns={columns}
                  data={tableData}
                  hidePagination={true}
                  // tableClass="table-sm"
                  verticalLines={true}
                  theadClass="table-light"
                  thClass="px-3 py-2"
                  divClass="mb-0"
                  tableClass="table-centered align-middle table-nowrap mb-0"
                />
              </div>
            )}

            {/* Action Buttons */}
            {/* Size Controls Row */}
            <Row className="d-flex align-items-end gap-2 mb-2">
              <div style={{ width: '150px' }}>
                <FormInput
                  id="newSize"
                  label={sizeLabel}
                  value={newSize}
                  onChange={(value) => setNewSize(value)}
                  type="text"
                  containerClassName="mb-0"
                />
              </div>
              <button
                onClick={handleAddSize}
                disabled={!newSize.trim()}
                className="btn btn-soft-primary btn-sm"
                style={{ minWidth: 120, width: 120 }}
              >
                Add Size
              </button>
              <button
                onClick={handleRemoveSize}
                className="btn btn-soft-danger btn-sm"
                style={{ minWidth: 120, width: 120 }}
              >
                Remove Size
              </button>
              <button
                onClick={handleReOrderAndRenameSize}
                className="btn btn-soft-secondary btn-sm"
                style={{ minWidth: 120, width: 120 }}
              >
                Re-Order & Rename
              </button>
              <button
                onClick={handleSelectAll}
                className="btn btn-soft-success btn-sm"
                style={{ minWidth: 120, width: 120 }}
              >
                Select All
              </button>
            </Row>

            {/* Color Controls Row */}
            <Row className="d-flex align-items-end gap-2 mb-2">
              <div style={{ width: '150px' }}>
                <FormInput
                  id="newColor"
                  label={colourLabel}
                  value={newColor}
                  onChange={(value) => setNewColor(value)}
                  type="text"
                  containerClassName="mb-0"
                />
              </div>
              <button
                onClick={handleAddColor}
                disabled={!newColor.trim()}
                className="btn btn-soft-primary btn-sm"
                style={{ minWidth: 120, width: 120 }}
              >
                Add Colour
              </button>
              <button
                onClick={handleRemoveColor}
                className="btn btn-soft-danger btn-sm"
                style={{ minWidth: 120, width: 120 }}
              >
                Remove Colour
              </button>
              <button
                onClick={handleClearAllChildren}
                className="btn btn-soft-warning btn-sm"
                style={{ minWidth: 120, width: 120 }}
              >
                Clear All Children
              </button>
              <button
                onClick={handleRemoveAll}
                className="btn btn-soft-danger btn-sm"
                style={{ minWidth: 120, width: 120 }}
              >
                Remove All
              </button>
            </Row>

            {/* Show More Button */}
            {/* <div className="text-center">
            <button 
              onClick={handleShowMore} 
              className="btn btn-link btn-sm"
              style={{ minWidth: 80 }}
            >
              Show More
            </button>
          </div> */}
          </fieldset>
        </Col>
      </Row>

      <FeatureTabSizeModal
        isOpen={isFeatureTabSizeModalOpen}
        onClose={() => setIsFeatureTabSizeModalOpen(false)}
        sizes={sizes}
        setSizes={setSizes}
        setFeatureMatrix={setFeatureMatrix}
        featureMatrix={featureMatrix}
      />
      <ApplyTemplateModal
        isOpen={isApplyTemplateModalOpen}
        onClose={() => setIsApplyTemplateModalOpen(false)}
      />
    </div>
  );
};

export default FeaturesTab;
